import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, AlertTriangle, Cuboid, Radio, Route, Truck, Video, WifiOff, X } from "lucide-react";
import {
  acknowledgeAlert,
  clearToken,
  connectFleetSocket,
  getComponentLayout,
  getCurrentUser,
  getGeofences,
  getOverview,
  getRoutes,
  getToken,
  getVehicleTrail,
} from "./api";
import { AlertRail } from "./components/AlertRail";
import { AlertSlaPanel } from "./components/AlertSlaPanel";
import { AnomalyPanel } from "./components/AnomalyPanel";
import { IncidentPanel } from "./components/IncidentPanel";
import { TripControlPanel } from "./components/TripControlPanel";
import { DriverPanel } from "./components/DriverPanel";
import { GeofencePanel } from "./components/GeofencePanel";
import { PermissionMatrixPanel } from "./components/PermissionMatrixPanel";
import { PartsInventoryPanel } from "./components/PartsInventoryPanel";
import { AuditPanel } from "./components/AuditPanel";
import { VehicleReadinessPanel } from "./components/VehicleReadinessPanel";
import { CameraTile } from "./components/CameraTile";
import { DeviceInventoryPanel } from "./components/DeviceInventoryPanel";
import { EvidencePanel } from "./components/EvidencePanel";
import { FuelPanel } from "./components/FuelPanel";
import { Login } from "./components/Login";
import { MaintenancePanel } from "./components/MaintenancePanel";
import { PredictiveMaintenancePanel } from "./components/PredictiveMaintenancePanel";
import { ProcurementPanel } from "./components/ProcurementPanel";
import { ReeferPanel } from "./components/ReeferPanel";
import { RiskPanel } from "./components/RiskPanel";
import { SecurityPanel } from "./components/SecurityPanel";
import { TopNav, type FleetFilter } from "./components/TopNav";
import { UserAdminPanel } from "./components/UserAdminPanel";
import { WorkshopPanel } from "./components/WorkshopPanel";
import { SystemsMatrix } from "./components/SystemsMatrix";
import { TirePanel } from "./components/TirePanel";
import { TrailerPanel } from "./components/TrailerPanel";
import { demoOverview, demoRoutes } from "./data/demo";
import type { AuthUser, ComponentLayout, Geofence, Overview, PositionSample, RouteCorridor, Sensor, Vehicle } from "./types";

const FleetMap = lazy(() =>
  import("./components/FleetMap").then((module) => ({ default: module.FleetMap })),
);
const VehicleTwin = lazy(() =>
  import("./components/VehicleTwin").then((module) => ({ default: module.VehicleTwin })),
);

function withVehicleMetrics(current: Overview, vehicles: Vehicle[]): Overview {
  return {
    ...current,
    vehicles,
    metrics: {
      ...current.metrics,
      total: vehicles.length,
      online: vehicles.filter((vehicle) => vehicle.status === "online").length,
      warning: vehicles.filter((vehicle) => vehicle.status === "warning").length,
      critical: vehicles.filter((vehicle) => vehicle.status === "critical").length,
      offline: vehicles.filter((vehicle) => vehicle.status === "offline").length,
      moving: vehicles.filter((vehicle) => vehicle.speed_kph > 5).length,
    },
  };
}

function positionOnDemoRoute(path: [number, number][], progress: number) {
  const segmentCount = path.length - 1;
  const scaled = Math.max(0, Math.min(0.999999, progress)) * segmentCount;
  const segment = Math.floor(scaled);
  const localProgress = scaled - segment;
  const start = path[segment];
  const end = path[segment + 1];
  const longitude = start[0] + (end[0] - start[0]) * localProgress;
  const latitude = start[1] + (end[1] - start[1]) * localProgress;
  const heading = (Math.atan2(end[0] - start[0], end[1] - start[1]) * 180 / Math.PI + 360) % 360;
  return { latitude, longitude, heading };
}

function Metric({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div className="metric" style={{ "--metric-accent": accent || "#3164ff" } as React.CSSProperties}>
      <span>{icon}</span>
      <div><small>{label}</small><strong>{String(value).padStart(2, "0")}</strong></div>
      <i />
    </div>
  );
}

function Dashboard({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const [overview, setOverview] = useState<Overview>(demoOverview);
  const [selectedId, setSelectedId] = useState(1);
  const [hasVehicleSelection, setHasVehicleSelection] = useState(false);
  const [vehicleVisualMode, setVehicleVisualMode] = useState<"cameras" | "model">("cameras");
  const [twinExpanded, setTwinExpanded] = useState(false);
  const [selectedSensorId, setSelectedSensorId] = useState<number | null>(null);
  const [componentLayout, setComponentLayout] = useState<ComponentLayout | null>(null);
  const [alertNotice, setAlertNotice] = useState<string | null>(null);
  const [fleetFilter, setFleetFilter] = useState<FleetFilter>("all");
  const [connected, setConnected] = useState(false);
  const [streamMode, setStreamMode] = useState<"live" | "simulator">("live");
  const [vehicleTrail, setVehicleTrail] = useState<PositionSample[]>([]);
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [routes, setRoutes] = useState<RouteCorridor[]>([]);
  const [userAdminOpen, setUserAdminOpen] = useState(false);
  const [deviceInventoryOpen, setDeviceInventoryOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [predictiveOpen, setPredictiveOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [tiresOpen, setTiresOpen] = useState(false);
  const [fuelOpen, setFuelOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [trailersOpen, setTrailersOpen] = useState(false);
  const [reefersOpen, setReefersOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);
  const [anomalyOpen, setAnomalyOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [tripOpen, setTripOpen] = useState(false);
  const [driverOpen, setDriverOpen] = useState(false);
  const [geofenceOpen, setGeofenceOpen] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [procurementOpen, setProcurementOpen] = useState(false);
  const [workshopOpen, setWorkshopOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const selectedIdRef = useRef(selectedId);
  const selectVehicle = useCallback((vehicle: Vehicle) => {
    setSelectedId(vehicle.id);
    setHasVehicleSelection(true);
  }, []);

  useEffect(() => {
    if (user.id === "demo") {
      setOverview(demoOverview);
      setRoutes(demoRoutes);
      setConnected(true);
      setStreamMode("simulator");
      return;
    }
    getOverview().then((data) => {
      setOverview(data);
      if (data.vehicles.length) setSelectedId(data.vehicles[0].id);
    });
    getGeofences().then(setGeofences);
    getRoutes().then(setRoutes);
    return connectFleetSocket((event) => {
      setConnected(true);
      const message = JSON.parse(event.data);
      if (message.type === "telemetry") {
        window.dispatchEvent(new CustomEvent("gsf:anomaly-updated"));
        if (
          message.sensors?.some(
            (sensor: { sensor_code: string }) => sensor.sensor_code.startsWith("tpms_"),
          )
        ) {
          window.dispatchEvent(new CustomEvent("gsf:tpms-updated"));
        }
        if (
          message.sensors?.some(
            (sensor: { sensor_code: string }) => sensor.sensor_code === "fuel_primary",
          )
        ) {
          window.dispatchEvent(new CustomEvent("gsf:fuel-updated"));
        }
        setStreamMode("live");
        setOverview((current) => {
          const vehicles = current.vehicles.map((vehicle) =>
            vehicle.id === message.vehicle_id
              ? { ...vehicle, ...message.position, last_seen: message.captured_at }
              : vehicle
          );
          const updated = withVehicleMetrics(current, vehicles);
          return {
            ...updated,
            latest_sensors: message.sensors?.length
            ? [
                ...message.sensors.map((sensor: Omit<Sensor, "id" | "vehicle_id" | "captured_at" | "metadata_json"> & { metadata?: Sensor["metadata_json"] }, index: number) => ({
                  ...sensor,
                  id: -(Date.now() + index),
                  vehicle_id: message.vehicle_id,
                  captured_at: message.captured_at,
                  metadata_json: sensor.metadata || {},
                })),
                ...current.latest_sensors.filter(
                  (existing) =>
                    existing.vehicle_id !== message.vehicle_id ||
                    !message.sensors.some(
                      (incoming: { sensor_code: string }) => incoming.sensor_code === existing.sensor_code,
                    ),
                ),
              ]
            : current.latest_sensors,
          };
        });
        if (
          message.vehicle_id === selectedIdRef.current &&
          message.position?.latitude != null &&
          message.position?.longitude != null
        ) {
          setVehicleTrail((current) => [
            ...current,
            {
              id: -Date.now(),
              vehicle_id: message.vehicle_id,
              ...message.position,
              captured_at: message.captured_at,
            },
          ].slice(-180));
        }
      }
      if (message.type === "fleet.positions") {
        if (message.source === "demo_simulator") setStreamMode("simulator");
        const positions = new Map<number, {
          latitude: number;
          longitude: number;
          speed_kph: number;
          heading: number;
          captured_at: string;
        }>(
          message.positions.map((position: {
            vehicle_id: number;
            latitude: number;
            longitude: number;
            speed_kph: number;
            heading: number;
            captured_at: string;
          }) => [position.vehicle_id, position]),
        );
        setOverview((current) => {
          const vehicles = current.vehicles.map((vehicle) => {
            const position = positions.get(vehicle.id);
            return position
              ? {
                  ...vehicle,
                  latitude: position.latitude,
                  longitude: position.longitude,
                  speed_kph: position.speed_kph,
                  heading: position.heading,
                  last_seen: position.captured_at,
                }
              : vehicle;
          });
          return withVehicleMetrics(current, vehicles);
        });
        const selectedPosition = message.positions.find(
          (position: { vehicle_id: number }) => position.vehicle_id === selectedIdRef.current,
        );
        if (selectedPosition) {
          setVehicleTrail((current) => {
            if (current.at(-1)?.captured_at === selectedPosition.captured_at) return current;
            return [
              ...current,
              {
                id: -Date.now(),
                vehicle_id: selectedPosition.vehicle_id,
                latitude: selectedPosition.latitude,
                longitude: selectedPosition.longitude,
                speed_kph: selectedPosition.speed_kph,
                heading: selectedPosition.heading,
                captured_at: selectedPosition.captured_at,
              },
            ].slice(-180);
          });
        }
      }
      if (message.type === "alert.created") {
        window.dispatchEvent(new CustomEvent("gsf:sla-updated"));
        setOverview((current) => {
          if (current.alerts.some((alert) => alert.id === message.alert.id)) return current;
          return {
            ...current,
            alerts: [message.alert, ...current.alerts].slice(0, 20),
            metrics: {
              ...current.metrics,
              open_alerts: (current.metrics.open_alerts || 0) + 1,
            },
          };
        });
        setAlertNotice(message.alert.title);
        window.setTimeout(() => setAlertNotice(null), 4800);
      }
      if (message.type === "alert.updated" || message.type === "alert.resolved") {
        window.dispatchEvent(new CustomEvent("gsf:sla-updated"));
        setOverview((current) => {
          const wasOpen = current.alerts.some((alert) => alert.id === message.alert.id);
          return {
            ...current,
            alerts: current.alerts.filter((alert) => alert.id !== message.alert.id),
            metrics: {
              ...current.metrics,
              open_alerts: Math.max(0, (current.metrics.open_alerts || 0) - (wasOpen ? 1 : 0)),
            },
          };
        });
      }
      if (message.type === "vehicle.connectivity") {
        setOverview((current) => {
          const vehicles = current.vehicles.map((vehicle) =>
            vehicle.id === message.vehicle_id
              ? {
                  ...vehicle,
                  status: message.status,
                  speed_kph: message.speed_kph,
                  last_seen: message.captured_at,
                }
              : vehicle,
          );
          return withVehicleMetrics(current, vehicles);
        });
      }
      if (message.type === "geofence.transition") {
        window.dispatchEvent(new CustomEvent("gsf:geofence-event-updated"));
        const action = message.event.transition === "entered" ? "Entrada" : "Salida";
        setAlertNotice(`${action} de geocerca registrada`);
        window.setTimeout(() => setAlertNotice(null), 3200);
      }
      if (message.type === "route.transition") {
        const notice = message.event.transition === "deviated"
          ? "Desviación de ruta detectada"
          : "Unidad reincorporada a su corredor";
        setAlertNotice(notice);
        window.setTimeout(() => setAlertNotice(null), 3800);
      }
      if (message.type === "maintenance.updated") {
        window.dispatchEvent(new CustomEvent("gsf:maintenance-updated"));
        setAlertNotice(`Orden de mantenimiento ${message.order.status}`);
        window.setTimeout(() => setAlertNotice(null), 3800);
      }
      if (message.type === "evidence.updated") {
        window.dispatchEvent(new CustomEvent("gsf:evidence-updated"));
        setAlertNotice("Clip de evidencia solicitado automáticamente");
        window.setTimeout(() => setAlertNotice(null), 3800);
      }
      if (message.type === "security.updated") {
        window.dispatchEvent(new CustomEvent("gsf:security-updated"));
        if (message.event.severity !== "normal") {
          setAlertNotice(
            message.event.event_type === "panic"
              ? "PÁNICO ACTIVADO · protocolo iniciado"
              : "Apertura de puerta detectada",
          );
          window.setTimeout(() => setAlertNotice(null), 4800);
        }
      }
      if (message.type === "trailer.paired") {
        window.dispatchEvent(new CustomEvent("gsf:trailer-updated"));
        setAlertNotice(`${message.trailer.unit_code} emparejado con ${message.trailer.assigned_unit_code}`);
        window.setTimeout(() => setAlertNotice(null), 3800);
      }
      if (message.type === "reefer.command.updated") {
        window.dispatchEvent(new CustomEvent("gsf:reefer-updated"));
        setAlertNotice(`Comando reefer · ${message.command.status}`);
        window.setTimeout(() => setAlertNotice(null), 3800);
      }
      if (message.type === "risk.updated") {
        window.dispatchEvent(new CustomEvent("gsf:risk-updated"));
      }
      if (message.type === "incident.updated") {
        window.dispatchEvent(new CustomEvent("gsf:incident-updated"));
      }
      if (message.type === "trip.updated") {
        window.dispatchEvent(new CustomEvent("gsf:trip-updated"));
      }
      if (message.type === "vehicle.readiness.updated") {
        window.dispatchEvent(new CustomEvent("gsf:readiness-updated"));
        window.dispatchEvent(new CustomEvent("gsf:trip-updated"));
      }
      if (message.type === "driver.updated") {
        window.dispatchEvent(new CustomEvent("gsf:driver-updated"));
      }
      if (message.type === "geofence.config.updated") {
        window.dispatchEvent(new CustomEvent("gsf:geofence-config-updated"));
        getGeofences().then(setGeofences);
      }
      if (message.type === "permissions.updated") {
        window.dispatchEvent(new CustomEvent("gsf:permissions-updated"));
      }
      if (message.type === "device.commissioning.updated") {
        window.dispatchEvent(new CustomEvent("gsf:commissioning-updated"));
      }
      if (message.type === "procurement.updated") {
        window.dispatchEvent(new CustomEvent("gsf:procurement-updated"));
      }
      if (message.type === "workshop.updated") {
        window.dispatchEvent(new CustomEvent("gsf:workshop-updated"));
        window.dispatchEvent(new CustomEvent("gsf:readiness-updated"));
      }
      if (message.type === "inventory.updated") {
        window.dispatchEvent(new CustomEvent("gsf:inventory-updated"));
        window.dispatchEvent(new CustomEvent("gsf:workshop-updated"));
      }
      if (message.type === "predictive.updated") {
        window.dispatchEvent(new CustomEvent("gsf:predictive-updated"));
      }
    });
  }, [user.id]);

  useEffect(() => {
    if (user.id !== "demo") return;
    let tick = 0;
    const timer = window.setInterval(() => {
      tick += 1;
      const capturedAt = new Date().toISOString();
      const baseProgress = [0.333, 0.5, 0.5, 0.2];
      setOverview((current) => {
        const vehicles = current.vehicles.map((vehicle, index) => {
          if (vehicle.status === "offline" || vehicle.speed_kph <= 5) return vehicle;
          const route = demoRoutes.find((item) => item.name === vehicle.route_name);
          if (!route) return vehicle;
          const progress = (baseProgress[index] + tick * (0.0025 + index * 0.00035)) % 2;
          const normalizedProgress = progress <= 1 ? progress : 2 - progress;
          const position = positionOnDemoRoute(route.path_json, normalizedProgress);
          return { ...vehicle, ...position, last_seen: capturedAt };
        });
        return withVehicleMetrics(current, vehicles);
      });
      const selectedIndex = demoOverview.vehicles.findIndex(
        (vehicle) => vehicle.id === selectedIdRef.current,
      );
      const selectedVehicle = demoOverview.vehicles[selectedIndex];
      const selectedRoute = demoRoutes.find((route) => route.name === selectedVehicle?.route_name);
      if (selectedVehicle && selectedRoute && selectedVehicle.speed_kph > 5) {
        const progress = (baseProgress[selectedIndex] + tick * (0.0025 + selectedIndex * 0.00035)) % 2;
        const normalizedProgress = progress <= 1 ? progress : 2 - progress;
        const position = positionOnDemoRoute(selectedRoute.path_json, normalizedProgress);
        setVehicleTrail((current) => [
          ...current,
          {
            id: -(Date.now() + selectedVehicle.id),
            vehicle_id: selectedVehicle.id,
            ...position,
            speed_kph: selectedVehicle.speed_kph,
            captured_at: capturedAt,
          },
        ].slice(-180));
      }
    }, 1200);
    return () => window.clearInterval(timer);
  }, [user.id]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
    let active = true;
    setVehicleTrail([]);
    getVehicleTrail(selectedId).then((positions) => {
      if (active) setVehicleTrail(positions);
    });
    return () => {
      active = false;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!twinExpanded) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTwinExpanded(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [twinExpanded]);

  useEffect(() => {
    const vehicle = overview.vehicles.find((item) => item.id === selectedId);
    if (!vehicle) return;
    getComponentLayout(vehicle.vehicle_type).then(setComponentLayout);
  }, [overview.vehicles, selectedId]);

  useEffect(() => {
    const candidates = overview.latest_sensors
      .filter((sensor) => sensor.vehicle_id === selectedId)
      .sort((a, b) => {
        const rank = { critical: 2, warning: 1, normal: 0 };
        return rank[b.severity] - rank[a.severity];
      });
    setSelectedSensorId((current) =>
      candidates.some((sensor) => sensor.id === current) ? current : candidates[0]?.id ?? null,
    );
  }, [overview.latest_sensors, selectedId]);

  const selected = overview.vehicles.find((vehicle) => vehicle.id === selectedId) || overview.vehicles[0];
  const selectedSensor = useMemo(
    () =>
      overview.latest_sensors.find(
        (sensor) => sensor.vehicle_id === selectedId && sensor.id === selectedSensorId,
      ) ||
      overview.latest_sensors.find(
        (sensor) => sensor.vehicle_id === selectedId && sensor.severity !== "normal",
      ) ||
      overview.latest_sensors.find((sensor) => sensor.vehicle_id === selectedId),
    [overview.latest_sensors, selectedId, selectedSensorId],
  );
  const filteredVehicles = useMemo(
    () =>
      overview.vehicles.filter((vehicle) => {
        if (fleetFilter === "moving") return vehicle.speed_kph > 5;
        if (fleetFilter === "parked_hq") return vehicle.metadata_json.location_state === "parked_hq";
        if (fleetFilter === "critical") return vehicle.status === "critical";
        if (fleetFilter === "offline") return vehicle.status === "offline";
        return true;
      }),
    [fleetFilter, overview.vehicles],
  );
  const cameraCandidates = overview.cameras
    .filter((camera) => camera.vehicle_id === selectedId)
    .slice(0, 4);
  const cameraPairs = cameraCandidates.map((camera) => ({
    camera,
    vehicle: overview.vehicles.find((vehicle) => vehicle.id === camera.vehicle_id),
  }));
  const canAcknowledge = ["superadmin", "fleet_admin", "operator", "dispatcher", "maintenance"].includes(
    user.role,
  );

  async function handleAcknowledge(alertId: number) {
    const alert = overview.alerts.find((item) => item.id === alertId);
    if (!alert) return;
    if (user.id === "demo") {
      setOverview((current) => ({
        ...current,
        alerts: current.alerts.filter((item) => item.id !== alertId),
        metrics: {
          ...current.metrics,
          open_alerts: Math.max(0, current.metrics.open_alerts - 1),
        },
      }));
      setAlertNotice(`Alerta reconocida · ${alert.title}`);
      window.setTimeout(() => setAlertNotice(null), 3200);
      return;
    }
    const updated = await acknowledgeAlert(alertId);
    setOverview((current) => {
      const wasOpen = current.alerts.some((item) => item.id === updated.id);
      return {
        ...current,
        alerts: current.alerts.filter((item) => item.id !== updated.id),
        metrics: {
          ...current.metrics,
          open_alerts: Math.max(0, (current.metrics.open_alerts || 0) - (wasOpen ? 1 : 0)),
        },
      };
    });
    setAlertNotice(`${alert.title} reconocida por ${user.full_name}`);
    window.setTimeout(() => setAlertNotice(null), 3200);
  }

  function handleFilterChange(filter: FleetFilter) {
    setFleetFilter(filter);
    const matches = overview.vehicles.filter((vehicle) => {
      if (filter === "moving") return vehicle.speed_kph > 5;
      if (filter === "parked_hq") return vehicle.metadata_json.location_state === "parked_hq";
      if (filter === "critical") return vehicle.status === "critical";
      if (filter === "offline") return vehicle.status === "offline";
      return true;
    });
    if (matches.length && !matches.some((vehicle) => vehicle.id === selectedId)) {
      setSelectedId(matches[0].id);
    }
  }

  if (!selected) return null;
  return (
    <main className="command-shell">
      <TopNav
        onLogout={onLogout}
        vehicles={overview.vehicles}
        selectedId={selectedId}
        onVehicleSelect={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
        activeFilter={fleetFilter}
        onFilterChange={handleFilterChange}
        alertCount={overview.alerts.length}
        canManageUsers={user.permissions?.includes("users.manage") ?? user.role === "superadmin"}
        canManagePermissions={user.permissions?.includes("permissions.manage") ?? user.role === "superadmin"}
        canViewAudit={user.permissions?.includes("audit.view") ?? ["superadmin", "fleet_admin", "auditor"].includes(user.role)}
        onOpenUsers={() => setUserAdminOpen(true)}
        onOpenDevices={() => setDeviceInventoryOpen(true)}
        onOpenMaintenance={() => setMaintenanceOpen(true)}
        onOpenWorkOrders={() => setWorkshopOpen(true)}
        onOpenInventory={() => setInventoryOpen(true)}
        onOpenPredictive={() => setPredictiveOpen(true)}
        onOpenEvidence={() => setEvidenceOpen(true)}
        onOpenSecurity={() => setSecurityOpen(true)}
        onOpenTrailers={() => setTrailersOpen(true)}
        onOpenReefers={() => setReefersOpen(true)}
        onOpenRisk={() => setRiskOpen(true)}
        onOpenSla={() => setSlaOpen(true)}
        onOpenAnomalies={() => setAnomalyOpen(true)}
        onOpenIncidents={() => setIncidentOpen(true)}
        onOpenTrips={() => setTripOpen(true)}
        onOpenVehicles={() => setReadinessOpen(true)}
        onOpenDrivers={() => setDriverOpen(true)}
        onOpenGeofences={() => setGeofenceOpen(true)}
        onOpenPermissions={() => setPermissionOpen(true)}
        onOpenAudit={() => setAuditOpen(true)}
        onOpenProcurement={() => setProcurementOpen(true)}
      />
      <div className="status-strip">
        <span><i className="status-dot is-online" /> PLATAFORMA OPERATIVA</span>
        <span>OPERADOR <b>{user.full_name.toUpperCase()}</b></span>
        <span>
          {connected ? <Radio size={12} /> : <WifiOff size={12} />}
          {connected
            ? streamMode === "simulator"
              ? "SIMULACIÓN EN VIVO"
              : "TIEMPO REAL"
            : "SIN CANAL EN VIVO"}
        </span>
        <time>{new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}</time>
      </div>
      <section className="metrics-grid">
        <Metric icon={<Truck size={17} />} label="UNIDADES" value={overview.metrics.total || 0} />
        <Metric icon={<Activity size={17} />} label="EN MOVIMIENTO" value={overview.metrics.moving || 0} accent="#59bf8b" />
        <Metric icon={<AlertTriangle size={17} />} label="CRÍTICAS" value={overview.metrics.critical || 0} accent="#ff554a" />
        <Metric icon={<Route size={17} />} label="EN RUTA" value={(overview.metrics.moving || 0) + 1} accent="#78a9d1" />
        <Metric icon={<WifiOff size={17} />} label="SIN SEÑAL" value={overview.metrics.offline || 0} accent="#7c8482" />
      </section>
      <section className="operations-grid">
        <div className="map-panel panel">
          <header className="section-header map-panel__header">
            <div>
              <span className="eyebrow">TEATRO NACIONAL DE OPERACIONES</span>
              <h2>Flotilla en tránsito</h2>
            </div>
            <div className="map-legend">
              <b>{filteredVehicles.length}/{overview.vehicles.length} visibles</b>
              <span><i className="status-dot is-online" /> Operativa</span>
              <span><i className="status-dot is-warning" /> Atención</span>
              <span><i className="status-dot is-critical" /> Crítica</span>
              <span><i className="status-dot" /> Sin señal</span>
            </div>
          </header>
          <Suspense fallback={<div className="visual-loading">CARGANDO MAPA OPERATIVO</div>}>
            <FleetMap
              vehicles={filteredVehicles}
              geofences={geofences}
              routes={routes}
              selectedId={selectedId}
              trail={vehicleTrail}
              onSelect={selectVehicle}
            />
          </Suspense>
          <div className="selected-unit">
            <span className={`status-pulse status-pulse--${selected.status}`} />
            <div>
              <small>UNIDAD SELECCIONADA</small>
              <strong>{selected.unit_code} · {selected.make} {selected.model}</strong>
              <span>
                {selected.driver_name} · {selected.route_name} · {vehicleTrail.length} puntos
              </span>
            </div>
            <b>{selected.speed_kph}<small> km/h</small></b>
          </div>
        </div>
        <section className="vehicle-visual-column">
          <nav className="vehicle-visual-tabs" aria-label="Vista visual del tracto">
            <button
              type="button"
              className={vehicleVisualMode === "cameras" ? "is-active" : ""}
              aria-pressed={vehicleVisualMode === "cameras"}
              onClick={() => setVehicleVisualMode("cameras")}
            >
              <Video size={14} /> CÁMARAS
            </button>
            <button
              type="button"
              className={vehicleVisualMode === "model" ? "is-active" : ""}
              aria-pressed={vehicleVisualMode === "model"}
              onClick={() => setVehicleVisualMode("model")}
            >
              <Cuboid size={14} /> MODELO 3D
            </button>
          </nav>
          {!hasVehicleSelection ? (
            <div className="vehicle-visual-placeholder">
              <Truck size={24} />
              <strong>SELECCIONA UN TRACTO</strong>
              <span>Elige una unidad en el mapa y después abre sus cámaras o modelo 3D.</span>
            </div>
          ) : vehicleVisualMode === "cameras" ? (
            <div className="camera-stack">
              {cameraPairs.map(({ camera, vehicle }) => (
                <CameraTile camera={camera} vehicle={vehicle} key={camera.id} />
              ))}
            </div>
          ) : (
            <Suspense fallback={<div className="vehicle-twin visual-loading">CARGANDO GEMELO 3D</div>}>
              <VehicleTwin
                vehicle={selected}
                sensors={overview.latest_sensors.filter((sensor) => sensor.vehicle_id === selected.id)}
                sensor={selectedSensor}
                layout={componentLayout}
                onSensorSelect={(sensor) => setSelectedSensorId(sensor.id)}
                onExpand={() => setTwinExpanded(true)}
              />
            </Suspense>
          )}
        </section>
        <AlertRail
          alerts={overview.alerts}
          vehicles={overview.vehicles}
          onSelect={selectVehicle}
          onAcknowledge={handleAcknowledge}
          canAcknowledge={canAcknowledge}
        />
        <SystemsMatrix
          vehicle={selected}
          sensors={overview.latest_sensors}
          selectedSensorId={selectedSensor?.id ?? null}
          onSensorSelect={(sensor) => setSelectedSensorId(sensor.id)}
          onOpenTires={() => setTiresOpen(true)}
          onOpenFuel={() => setFuelOpen(true)}
        />
      </section>
      {twinExpanded && hasVehicleSelection && (
        <div
          className="twin-detail-overlay"
          role="presentation"
          onMouseDown={() => setTwinExpanded(false)}
        >
          <div
            className="twin-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Gemelo digital detallado de ${selected.unit_code}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="twin-detail-close"
              onClick={() => setTwinExpanded(false)}
              aria-label="Cerrar gemelo digital"
            >
              <X size={18} />
            </button>
            <Suspense fallback={<div className="visual-loading">CARGANDO GEMELO 3D</div>}>
              <VehicleTwin
                vehicle={selected}
                sensors={overview.latest_sensors.filter((sensor) => sensor.vehicle_id === selected.id)}
                sensor={selectedSensor}
                layout={componentLayout}
                detailed
                onSensorSelect={(sensor) => setSelectedSensorId(sensor.id)}
              />
            </Suspense>
          </div>
        </div>
      )}
      {alertNotice && <div className="command-toast" role="status">{alertNotice}</div>}
      <UserAdminPanel open={userAdminOpen} onClose={() => setUserAdminOpen(false)} />
      <DeviceInventoryPanel
        open={deviceInventoryOpen}
        onClose={() => setDeviceInventoryOpen(false)}
        vehicles={overview.vehicles}
        canManage={user.permissions?.includes("devices.manage") ?? ["superadmin", "fleet_admin", "maintenance"].includes(user.role)}
      />
      <MaintenancePanel
        open={maintenanceOpen}
        onClose={() => setMaintenanceOpen(false)}
        vehicles={overview.vehicles}
      />
      <WorkshopPanel
        open={workshopOpen}
        onClose={() => setWorkshopOpen(false)}
        vehicles={overview.vehicles}
        user={user}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <PartsInventoryPanel
        open={inventoryOpen}
        onClose={() => setInventoryOpen(false)}
        user={user}
      />
      <PredictiveMaintenancePanel
        open={predictiveOpen}
        onClose={() => setPredictiveOpen(false)}
        vehicles={overview.vehicles}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <EvidencePanel
        open={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        vehicles={overview.vehicles}
      />
      <TirePanel
        open={tiresOpen}
        onClose={() => setTiresOpen(false)}
        vehicle={selected}
      />
      <FuelPanel
        open={fuelOpen}
        onClose={() => setFuelOpen(false)}
        vehicle={selected}
      />
      <SecurityPanel
        open={securityOpen}
        onClose={() => setSecurityOpen(false)}
        vehicles={overview.vehicles}
      />
      <TrailerPanel
        open={trailersOpen}
        onClose={() => setTrailersOpen(false)}
      />
      <ReeferPanel
        open={reefersOpen}
        onClose={() => setReefersOpen(false)}
        user={user}
      />
      <RiskPanel
        open={riskOpen}
        onClose={() => setRiskOpen(false)}
        vehicles={overview.vehicles}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <AlertSlaPanel
        open={slaOpen}
        onClose={() => setSlaOpen(false)}
        vehicles={overview.vehicles}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <AnomalyPanel
        open={anomalyOpen}
        onClose={() => setAnomalyOpen(false)}
        vehicles={overview.vehicles}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <IncidentPanel
        open={incidentOpen}
        onClose={() => setIncidentOpen(false)}
        vehicles={overview.vehicles}
        user={user}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <TripControlPanel
        open={tripOpen}
        onClose={() => setTripOpen(false)}
        vehicles={overview.vehicles}
        user={user}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <VehicleReadinessPanel
        open={readinessOpen}
        onClose={() => setReadinessOpen(false)}
        vehicles={overview.vehicles}
        user={user}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <DriverPanel
        open={driverOpen}
        onClose={() => setDriverOpen(false)}
        vehicles={overview.vehicles}
        user={user}
        onSelectVehicle={(vehicle) => {
          setFleetFilter("all");
          selectVehicle(vehicle);
        }}
      />
      <GeofencePanel
        open={geofenceOpen}
        onClose={() => setGeofenceOpen(false)}
        user={user}
        onGeofencesChange={() => getGeofences().then(setGeofences)}
      />
      <PermissionMatrixPanel
        open={permissionOpen}
        onClose={() => setPermissionOpen(false)}
      />
      <AuditPanel
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
      />
      <ProcurementPanel
        open={procurementOpen}
        onClose={() => setProcurementOpen(false)}
        user={user}
      />
    </main>
  );
}

export default function App() {
  const [user, setUser] = useState<AuthUser | null | undefined>(
    getToken() ? undefined : null,
  );

  useEffect(() => {
    if (user !== undefined) return;
    getCurrentUser()
      .then(setUser)
      .catch(() => {
        clearToken();
        setUser(null);
      });
  }, [user]);

  if (user === undefined) {
    return <main className="session-loading"><span>QUASARYX</span><b>VALIDANDO SESIÓN C5</b></main>;
  }
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={() => setUser(null)} />;
}
